import { autoinject, computedFrom, observable, transient } from "aurelia-framework";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { I18N } from "aurelia-i18n";
import { RightShipMultiLookupModels, MultiLookupModel } from "../../../components/multi-checked-list-selector/multi-checked-list-selector";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { VoyageDataSubmissionService } from "../../../../services/vetting/voyage-data-submission.service";
import * as moment from "moment";
import * as Enumerable from "linq";
import { CarbonAccountingEmissionsIntensityReportResponse, CargoTypeDistributionModel } from "../../../../models/vetting/vetting.models";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import {VoyageService} from "../../../../services/voyage/voyage.service";
import { CarbonAccountingEnrollmentService } from "../../../../services/carbon-accounting/carbon-accounting-enrollment.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import {
    CarbonAccountingCustomReportFilterModel,
    FinancialYearTypeEnum
} from "../../../../models/carbon-accounting/carbon-accounting.models";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { Guid } from "../../../../utilities/guid";
import { NumberFormatHelper } from "../../../../helpers/number-format-helper";
import { NotifyPropertyChangedBase, Reflection } from "../../../../utilities/reflection";
import "amcharts3";
import "amcharts3/amcharts/xy.js";
import "amcharts3/amcharts/plugins/animate/animate.min.js";
import "amcharts3/amcharts/plugins/dataloader/dataloader.min.js";
import "amcharts3/amcharts/plugins/responsive/responsive.min.js";

const amcharts3 = window["AmCharts"];

@autoinject
@transient()
export class CarbonAccountingEmissionsIntensity extends NotifyPropertyChangedBase {

    vesselTypeDisplayText: string;
    vesselTypeDescription: string = "";
    cachedMultipleLookups: { [lookup: string]: MultiLookupModel[] } = {};
    selectedVesselType: { shipTypeId?: number[], vesselTypeGroupId?: number[] } = {};

    chart: any;
    peerChart: HTMLElement;

    @observable
    selectedEmissionIntensity: LookupValue;

    @observable
    selectedSizeGroupeType: LookupValue;
    isRightShipUser: boolean = false;
    showYearRangeFilter: boolean = false;
    showYearFilter: boolean = false;
    isQuarterly: boolean = false;
    yearRangeIsApplied: boolean = false;
    yearRangeFilterActive: boolean = false;
    isMobileFilterOpen: boolean = false;
    showFallback: boolean = false;

    selectedCustomerId?: number;
    yearRangeDefaults: { low: number, high: number } = { low: moment().year() - 4, high: moment().year() };
    mobileYearRangeFilter: { low: number, high: number };
    @observable()
    yearFirstFilter: number = this.yearRangeDefaults.low;
    @observable()
    yearSecondFilter: number = this.yearRangeDefaults.high;
    chartFromYear: number = this.yearFirstFilter;
    chartToYear: number = this.yearSecondFilter;
    reportTypes: { id: number, name: string }[] = [
        {
            id: 1,
            name: "Quarterly"
        }, {
            id: 2,
            name: "Year on Year"
        },
    ];
    computedUnits: { id: number, name: string, unit: string }[] = [
        {
            id: 1,
            name: "DWT",
            unit: "t"
        }, 
        {
            id: 2,
            name: "TEU",
            unit: "teu"
        },
        {
            id: 3,
            name: "CBM",
            unit: "cbm"
        },
    ];
    selectedReportType: { id: number, name: string } = this.reportTypes[0];
    selectedComputedUnit: { id: number, name: string, unit: string } = this.computedUnits[0];
    
    @observable
    year: any;
    private customerFinancialYearTypeId: any;
    private highestAxisNumber: number;
    private lowestAxisNumber: number;
    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.yearRangeDefaults.low),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.yearRangeDefaults.high),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.customerFinancialYearTypeId))

    get years() {
        const yearsRange = Enumerable.range(this.yearRangeDefaults.low - 20, (this.yearRangeDefaults.high - this.yearRangeDefaults.low) + 21).orderByDescending(x => x).toArray();
        const financialYearsToDisplay: string[] = [];
        yearsRange.forEach(x=> {
            const financialYear = x - 1;

            financialYearsToDisplay.push(this.customerFinancialYearTypeId == FinancialYearTypeEnum.January? x.toString() : financialYear.toString() + '-' + x.toString())
        });

        return financialYearsToDisplay;
    }

    getCustomerEnrollmentData() {
        if (this.selectedCustomerId || !this.isRightShipUser) {
            this.carbonAccountingService.GetCaEnrollmentDataForSummary(this.selectedCustomerId).then(response => {
                if (!response)
                    return;
                this.customerFinancialYearTypeId = response.financialYearTypeId;
            });
        }
    }
    
    async attached() {
        this.getCustomerEnrollmentData();
        this.voyageService.getCarbonAccountingVoyageSubmissionYears(this.selectedCustomerId ? this.selectedCustomerId : 0)
            .then(r => {
                const maxYear = Math.max.apply(Math, [...new Set(r.voyageSubmissionYears)]);
                this.year = this.customerFinancialYearTypeId == FinancialYearTypeEnum.January ? maxYear.toString() : (maxYear - 1).toString() + '-' + maxYear;
            });
        await this.refreshChartData();
        await this.getCustomFilters();
        await this.reloadSizeGroupTypes();
    }

    selectedCustomerChanged() {
        this.getCustomFilters();
        this.getCustomerEnrollmentData();
        this.voyageService.getCarbonAccountingVoyageSubmissionYears(this.selectedCustomerId ? this.selectedCustomerId : 0)
            .then(r => {
                const maxYear =  Math.max.apply(Math, [...new Set(r.voyageSubmissionYears)]);
                this.year = this.customerFinancialYearTypeId == FinancialYearTypeEnum.January ? maxYear.toString() : (maxYear - 1).toString() + '-' + maxYear;
            });
        this.refreshChartData();
    }

    yearFirstFilterChanged(){
        window.setTimeout(() => {
            this.yearSecondFilter = (this.yearFirstFilter) + 3;
        }, 500)
    }
    
    yearSecondFilterChanged(){
        window.setTimeout(() => {
            this.yearFirstFilter = (this.yearSecondFilter) - 3;
        }, 500)
    }
    
    selectedEmissionIntensityChanged() {
        this.refreshChartData();
    }

    selectedSizeGroupeTypeChanged() {
        this.refreshChartData();
    }

    yearChanged() {
        this.refreshChartData();
    }

    reportTypeChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.item.id === $event.detail.oldValue.id))
            return;

        const reportTypeId = $event.detail.item.id;
        switch (reportTypeId) {
            case 1:
                this.showYearFilter = true;
                this.showYearRangeFilter = false;
                this.yearRangeIsApplied = false;
                this.isQuarterly = true;
                break;

            case 2:
                this.showYearFilter = false;
                this.showYearRangeFilter = true;
                this.yearRangeIsApplied = true;
                this.isQuarterly = false;
                break;
        }

        this.yearRangeFilterActive = false;
        this.refreshChartData();
    }

    computedUnitChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.item.id === $event.detail.oldValue.id))
            return;
        
        this.refreshChartData();
    }

    mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();
        this.refreshChartData();
    }

    applyYearRangeFilter(filterValue: { low: number, high: number } = null) {

        if (filterValue) {
            this.yearFirstFilter = filterValue.low;
            this.yearSecondFilter = filterValue.high;
            this.yearRangeIsApplied = true;
        }

        if (this.yearSecondFilter - this.yearFirstFilter > 4) {
            this.yearFirstFilter = this.yearSecondFilter - 3;
        }
        
        this.chartFromYear = this.yearFirstFilter;
        this.chartToYear = this.yearSecondFilter;

        this.toggleYearRangeFilters();
        this.refreshChartData();
    }

    public clearYearRangeFilter() {
        this.yearFirstFilter = this.yearRangeDefaults.low;
        this.yearSecondFilter = this.yearRangeDefaults.high;
        this.mobileYearRangeFilter = { low: null, high: null };
        this.yearRangeIsApplied = false;

        this.toggleYearRangeFilters();
        this.refreshChartData();
    }

    toggleMobileFilterDialog() {
        if (!this.mobileFilterSelected) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        if(this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(x => x["open"] = false)

        this.yearRangeFilterActive = false;
        this.onPropertyChanged<CarbonAccountingEmissionsIntensity>(z => z.customFilters);
    }

    closeAllFilters() {
        this.yearRangeFilterActive = false;
        
        if(this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(x => {
                x["open"] = false;
            });
        this.onPropertyChanged<CarbonAccountingEmissionsIntensity>(z => z.customFilters);
    }

    toggleYearRangeFilters() {
        this.yearRangeFilterActive = !this.yearRangeFilterActive;
        this.selectedFilter = {
            title: "Date Range",
            clear: () => {
                this.clearYearRangeFilter();
            },
            apply: () => {
                const filterValue: { low: number, high: number } = { low: this.mobileYearRangeFilter.low, high: this.mobileYearRangeFilter.high}
                this.applyYearRangeFilter(filterValue);
            }
        }
    }

    clearAllFilters() {
        this.yearRangeFilterActive = false;
        this.selectedEmissionIntensity = {id:1, name:"AER"};
        this.selectedSizeGroupeType = {id:null, name:"ALL"};
        this.selectedVesselType = {};
        if(this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(filter => {
                filter["selectedItem"] = null;
                filter["selectedValue"] = null;
                filter["open"] = false;
            });
        this.onPropertyChanged<CarbonAccountingEmissionsIntensity>(z => z.customFilters);
        this.reloadSizeGroupTypes();
        this.refreshChartData();
    }
    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedVesselType),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedVesselType.vesselTypeGroupId),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedVesselType.vesselTypeGroupId.length),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedVesselType.shipTypeId),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedVesselType.shipTypeId.length))

    public get vesselTypeFilterActive() {
        return this.selectedVesselType && ((this.selectedVesselType.vesselTypeGroupId && this.selectedVesselType.vesselTypeGroupId.length > 0) ||
            (this.selectedVesselType.shipTypeId && this.selectedVesselType.shipTypeId.length > 0));
    }

    constructor(private dialogClientService: DialogClientService, private i18N: I18N, private voyageDataSubmissionService: VoyageDataSubmissionService,
        private router: Router, userClaimsService: UserClaimsService, private voyageService: VoyageService,
        private carbonAccountingService: CarbonAccountingEnrollmentService, private customerService: CommonCustomerService, private lookupService: LookupClientService) {
            super();
            this.vesselTypeDisplayText = this.i18N.tr('vessel-type');
            this.isRightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
         }

    showWhatIsEmissionsIntensityDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.WhatIsEmissionsIntensityDialog, this.selectedEmissionIntensity.name, true);
    }

    data: CarbonAccountingEmissionsIntensityReportResponse;
    isLoadingData: boolean = true;
    async refreshChartData() {
        this.showFallback = false;
        this.isLoadingData = true;

        const customFilterData = {};
        if(this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(f => {
                customFilterData[f.internalColumn.substring(f.internalColumn.length - 1)] = f["selectedValue"] && f["selectedValue"].trim() != ""? f["selectedValue"]: null;
            });
        
        try {
            this.data = await this.voyageDataSubmissionService.getCarbonAccountingEmissionsIntensityReport({
                emissionIntensityId: this.selectedEmissionIntensity ? this.selectedEmissionIntensity.id : null,
                vesselTypeGroupId: this.selectedVesselType ? this.selectedVesselType.vesselTypeGroupId : null,
                vesselTypeId: this.selectedVesselType ? this.selectedVesselType.shipTypeId : null,
                sizeGroupeTypeId: this.selectedSizeGroupeType ? this.selectedSizeGroupeType.id : null,
                reportTypeId: this.selectedReportType ? this.selectedReportType.id : null,
                yearFrom: this.showYearRangeFilter ? this.yearFirstFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January? this.year: (this.year? this.year.split('-')[1] : null)),
                yearTo: this.showYearRangeFilter ? this.yearSecondFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January? this.year: (this.year? this.year.split('-')[1] : null)),
                isQuarterly: this.isQuarterly,
                customerId: this.selectedCustomerId,
                customFilterValue1: Object.keys(customFilterData).indexOf("1") > -1? customFilterData["1"]: null,
                customFilterValue2: Object.keys(customFilterData).indexOf("2") > -1? customFilterData["2"]: null,
                customFilterValue3: Object.keys(customFilterData).indexOf("3") > -1? customFilterData["3"]: null,
                customFilterValue4: Object.keys(customFilterData).indexOf("4") > -1? customFilterData["4"]: null,
                customFilterValue5: Object.keys(customFilterData).indexOf("5") > -1? customFilterData["5"]: null,
                customFilterValue6: Object.keys(customFilterData).indexOf("6") > -1? customFilterData["6"]: null,
                cargoUnit: this.selectedComputedUnit.name
            });
            this.data.caEmissionsIntensityReportSummary = this.data.caEmissionsIntensityReportSummary.filter(x=> x.computedUnit == this.selectedComputedUnit.name)
            try { this.prepareChart() } catch (e) { console.error(e); }
            const allValuesEmpty = (this.selectedEmissionIntensity?.name == 'AER' || this.selectedEmissionIntensity?.name == 'EEOI') && !Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).where(x => x.average > 0).any();
            this.showFallback = this.data.caEmissionsIntensityReportPerVessel.length == 0 && this.data.caEmissionsIntensityReportSummary.length == 0 || allValuesEmpty;
        }
        catch (e) {
            console.error(e);
            this.showFallback = true;
        }
        finally {
            this.isLoadingData = false;
        }
    }

    public getColor(rating: string) {
        rating = rating.toLowerCase();
        if (rating == "a") {
            return "#00763B";
        } else if (rating == "b") {
            return "#AED36B";
        } else if (rating == "c") {
            return "#FFDD15";
        } else if (rating == "d") {
            return "#FFAA1C";
        } else if (rating == "e") {
            return "#D71920";
        } else if (rating == "f") {
            return "#FC6B37";
        } else if (rating == "g") {
            return "#D71920";
        } else if (rating == "u") {
            return "#42526E";
        }
    }

    private prepareChart() {
        if (!this.peerChart || !this.data || this.data.caEmissionsIntensityReportPerVessel.length == 0) {
            return;
        }

        const maximumWeight = Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).max(c => c.dwt);
        const minimumWeight = Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).min(c => c.dwt);
        let steps = maximumWeight / 1000;
        steps = steps > 1 ? steps : 1;
        const weights = Enumerable.range(1, maximumWeight / steps, steps).where(weight => weight >= minimumWeight).toArray();
        let maximum = Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).max(c => c.average);
        const minimum = Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).min(c => c.average);

        const dataProvider = [];
        const trajectoryValue = this.data.caEmissionsIntensityReportSummary[0] ? this.data.caEmissionsIntensityReportSummary[0].trajectoryValue : null;
        if (maximum < trajectoryValue) {
            maximum = trajectoryValue;
        }
        if (maximum < this.selectedSizeGroupeTypeAvg) {
            maximum = this.selectedSizeGroupeTypeAvg;
        }

        for (const weight of weights) {
            dataProvider.push({
                trajectoryValue: trajectoryValue,
                totalAvg: this.selectedSizeGroupeTypeAvg,
                label: weight
            });
        }

        for (const item of this.data.caEmissionsIntensityReportPerVessel) {
            dataProvider.push({
                average: item.average,
                label: item.dwt,
                color: this.getColor(item.ghgRating),
                vesselName: item.vesselName,
                voyages: item.voyages,
                ghgRating: item.ghgRating,
                vesselImo: item.imo,
                vesselId: item.vesselSkey
            });
        }

        const chartAxes = [{
            "id": "y-axis",
            "axisAlpha": 0,
            "dashLength": 3,
            "title": this.selectedEmissionIntensity.name,
            "maximum": maximum + (maximum / 100),
            "minimum": minimum - (maximum / 100)
        }, {
            "id": "x-axis",
            "axisAlpha": 0,
            "position": "bottom",
            "title": "DWT",
            "gridCount": 21,
            "gridThickness": 0,
            "autoGridCount": false,
            "maximum": maximumWeight + (maximumWeight / 100),
            "minimum": minimumWeight - (maximumWeight / 100),
            "labelFunction": (value) => {
                if (!value) {
                    return value;
                }
                if (value > 1000) {
                    return (Number(value) / 1000) + "K";
                }
                return value;
            }
        }];

        this.chart = amcharts3.makeChart(this.peerChart, {
            "type": "xy",
            "addClassNames": true,
            "balloon": {
                "disableMouseEvents": false,
                "fadeOutDuration": 1.5
            },
            "graphs": [{
                "bullet": "none",
                "lineAlpha": 0.7,
                "lineThickness": 2,
                "lineColor": "#F7661F",
                "xField": "label",
                "yField": "trajectoryValue",
                "balloonText": null,
                "bulletAlpha": 0,
                "bulletSize": 0,
                "maxBulletSize": 0,
                "labelText": null
            }, {
                "bullet": "none",
                "lineAlpha": 0.7,
                "lineThickness": 2,
                "lineColor": "#42526E",
                "xField": "label",
                "yField": "totalAvg",
                "balloonText": null,
                "bulletAlpha": 0,
                "bulletSize": 0,
                "maxBulletSize": 0,
                "labelText": null
            }, {
                "lineThickness": 0,
                "bullet": "round",
                "xField": "label",
                "yField": "average",
                "balloonFunction": (item) => {
                    let yearFrom = this.showYearRangeFilter ? this.yearFirstFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January? this.year: (this.year? this.year.split('-')[1] : null));
                    let yearTo = this.showYearRangeFilter ? this.yearSecondFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January? this.year: (this.year? this.year.split('-')[1] : null));

                    if (!yearFrom) yearFrom = "";
                    if (!yearTo) yearTo = "";

                    let txt = `${item.dataContext.vesselName}, IMO ${item.dataContext.vesselImo}`;
                    txt += `<br/>DWT: ${item.dataContext.label}`;
                    txt += `<br/>${this.selectedEmissionIntensity.name}: ${NumberFormatHelper.getFormattedValue(item.dataContext.average, null, 2, 0)}`;
                    txt += `<br/>Voyages: <a style="font-size: 11px; text-decoration: underline;" target="_blank" href="carbon-accounting-voyage-records?vesselId=${item.dataContext.vesselId}&yearFrom=${yearFrom}&yearTo=${yearTo}&customerId=${this.selectedCustomerId ?? this.userCustomerId}">${item.dataContext.voyages}</a>`;
                    txt += `<br/>GHG Rating: ${item.dataContext.ghgRating}`;
                    return txt;
                },
                "bulletAlpha": 1,
                "bulletSize": 16,
                "maxBulletSize": 16,
                "colorField": "color"
            }],
            "valueAxes": chartAxes,
            "dataProvider": dataProvider
        });
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get lowest() {
        return this.data && this.data.caEmissionsIntensityReportPerVessel ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).orderBy(x => x.average).firstOrDefault() : null;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get lowestFive() {
        return this.data && this.data.caEmissionsIntensityReportPerVessel ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).orderBy(x => x.average).take(5).toArray() : null;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get highest() {
        return this.data && this.data.caEmissionsIntensityReportPerVessel ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).orderByDescending(x => x.average).firstOrDefault() : null;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get highestFive() {
        return this.data && this.data.caEmissionsIntensityReportPerVessel ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).orderByDescending(x => x.average).take(5).toArray() : null;
    } 
 
    get max() { 
        if(this.data && this.data.caEmissionsIntensityReportSummary && this.data.caEmissionsIntensityReportSummary.length > 0){
            const avg = Enumerable.from(this.data.caEmissionsIntensityReportSummary).max(x => x.average);
            const imoTrajectory = this.data.caEmissionsIntensityReportSummary[0].trajectoryValue;
            if (avg >= imoTrajectory) {
                return avg;
            }
            else if (avg < imoTrajectory) {
                return imoTrajectory;
            }
        }
        return 0;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.max)) 
    get summaryStep() { 
        return Math.max(Math.ceil(this.max / 10), 1);
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.summaryStep),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.max)) 
    get ceil() {
        return Math.ceil(Math.max(this.max, 10) + this.summaryStep);
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.ceil),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.summaryStep)) 
    get summaryAxis() {
        return Enumerable.range(0, this.ceil / this.summaryStep, this.summaryStep).orderByDescending(x => x).toArray();
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.isQuarterly),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.chartFromYear),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.chartToYear))
    get summaryAxisHorizontal() {
        return this.isQuarterly ? ["Q1", "Q2", "Q3", "Q4"] : Enumerable.range(this.chartFromYear, this.chartToYear - this.chartFromYear + 1).select(x => x.toString()).toArray();
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.lowestFive), 
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.lowestFive.length)) 
    get lowestAxis() {
        const max = Enumerable.from(this.lowestFive).any() ? Enumerable.from(this.lowestFive).max(x => x.average) : 0;
        const step = Math.max(Math.ceil(max / 5), 1);
        const range = Enumerable.range(step, 5, step).orderBy(x => x).toArray()
        this.lowestAxisNumber = Enumerable.from(range).orderBy(x=> x).lastOrDefault()
        return range;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.highestFive), 
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.highestFive.length)) 
    get highestAxis() {
        const max = Enumerable.from(this.highestFive).any() ? Enumerable.from(this.highestFive).max(x => x.average) : 0;
        const step = Math.max(Math.ceil(max / 5), 1);
        const range = Enumerable.range(step, 5, step).orderBy(x => x).toArray()
        this.highestAxisNumber = Enumerable.from(range).orderBy(x=> x).lastOrDefault();
        return range;
    }

    getLowestWidth(bar) {
        const max = Math.max(this.lowestAxisNumber, 5);
        return bar.average / max * 100;
    }

    getHighestWidth(bar) {
        const max = Math.max(this.highestAxisNumber, 5);
        return bar.average / max * 100;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary.length),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.summaryAxisHorizontal),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.summaryAxisHorizontal.length),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.isQuarterly)) 
    get summary() {
        if (this.data && this.data.caEmissionsIntensityReportSummary) {
            if (this.isQuarterly)
                return this.summaryAxisHorizontal.map(x => {
                    const val = this.data.caEmissionsIntensityReportSummary.filter(a => `Q${a.periodValue}` == x)[0];
                    return val ? val : 0;
                });
            return Enumerable.from(this.summaryAxisHorizontal).select(x => {
                const val = this.data.caEmissionsIntensityReportSummary.filter(a => a.periodValue == Number(x))[0];
                return val ? val : 0;
            }).toArray();
        }
        return [];
    }

    @computedFrom('data', 'data.caEmissionsIntensityReportSummary')
    get trajectoryPositionInPercentage() {
        if (!this.data || !this.data.caEmissionsIntensityReportSummary || this.data.caEmissionsIntensityReportSummary.length == 0 
            || !this.data.caEmissionsIntensityReportSummary[0] || this.data.caEmissionsIntensityReportSummary[0].trajectoryValue == 0) {
            return 0;
        }

        let computedCeil = this.ceil - this.summaryStep;
        computedCeil = computedCeil != 0 ? computedCeil : 1;
        const result = this.data.caEmissionsIntensityReportSummary[0].trajectoryValue/computedCeil * 100;
        return result;
    }

    openVesselTypesDialog() {
        const lookup = LookupValueQuery.VesselTypes;
        const displayText = this.i18N.tr('vessel-type');
        const cachedMultiLookups = this.cachedMultipleLookups[lookup] ? this.cachedMultipleLookups[lookup] : RightShipMultiLookupModels.getMultiLookupModel(lookup);
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            multiLookups: cachedMultiLookups,
            title: displayText,
            selectedIds: this.selectedVesselType,
            onApply: (multiLookups: MultiLookupModel[]) => {

                this.selectedVesselType = {};

                const result = RightShipMultiLookupModels.getMultiLookupFilters(lookup, displayText, multiLookups);
                if (result) {
                    for (const filter of result.filters) {
                        this.selectedVesselType[filter.filterProperty.name] = filter.filterValue;
                    }
                }
                this.vesselTypeDisplayText = result.filterDisplayText;
                this.vesselTypeDescription = result.filterDescription;
                this.cachedMultipleLookups[lookup] = multiLookups;
                this.reloadSizeGroupTypes();
                this.refreshChartData();
            }
        }, true, false, false, 'right');
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedSizeGroupeType),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.vesselTypeFilterActive),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedSizeGroupeType.name))
    get showPerformanceSection() {
        if (this.selectedSizeGroupeType) {
            return this.selectedSizeGroupeType.name != "ALL" && this.vesselTypeFilterActive != null
        }
        return false;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedEmissionIntensity))
    get selectedSizeGroupeTypeAvg() {
        if (!this.data || !this.data.caEmissionsIntensityReportPerVessel || !this.selectedEmissionIntensity)
            return 0;

        // Return Imo ghg emissions if the type of emission type is not Intensity
        const ghgEmissionsArray = this.selectedEmissionIntensity.id == 3 ? this.data.caEmissionsIntensityReportPerVessel.map(x => x.totalGhgEmissions) 
            : this.data.caEmissionsIntensityReportPerVessel.map(x => x.totalImoGhgEmissions);

        // Multiple current value of GhgEmissions by 1,000,000 only if emission type is Intensity
        const totalGhgSum = !ghgEmissionsArray || ghgEmissionsArray.length == 0 ? 0 : this.selectedEmissionIntensity.id == 3 ?
            ghgEmissionsArray.reduce((sum, current) => sum + (current * 1000000), 0) :
            ghgEmissionsArray.reduce((sum, current) => sum + current, 0)

        const totalDenominatorArray = this.data.caEmissionsIntensityReportPerVessel.map(x => x.totalDenominator)
        const totalDenominatorSum = totalDenominatorArray && totalDenominatorArray.length > 0 ? totalDenominatorArray.reduce((sum, current) => sum + current) : 0;

        return totalGhgSum / (totalDenominatorSum == 0 ? 1 : totalDenominatorSum);
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get selectedSizeGroupeTypeEmissions() {
        return this.data && this.data.caEmissionsIntensityReportPerVessel ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).sum(x => x.totalGhgEmissions) : 0;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get selectedSizeGroupeTypeVoyages() {
        return this.data && this.data.caEmissionsIntensityReportPerVessel ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).sum(x => x.voyages) : 0;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get selectedSizeGroupeTypeDistance() {
        return this.data && this.data.caEmissionsIntensityReportPerVessel ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel).sum(x => x.totalDistance) : 0;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length))
    get selectedSizeGroupeTypeCargoType() {
        return this.data && this.data.caReportPerCargoTypeForEmissionsIntensity ? Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity).groupBy(x => x.cargoTypeSkey).count() : 0;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caReportPerCargoTypeForEmissionsIntensity),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caReportPerCargoTypeForEmissionsIntensity.length))
    get selectedSizeGroupeTypeCargoTypeQuantity() {
        return this.data && this.data.caReportPerCargoTypeForEmissionsIntensity ? Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity)
            .distinct(y=> y.totalCargo)
            .sum(x => x.totalCargo) : 0;
    }

    viewCargoTypesDialog() {
        const cargoTypeDistributions: CargoTypeDistributionModel[] = [];

        if (!this.data || !this.data.caReportPerCargoTypeForEmissionsIntensity)
            return;

        const cargoTypes = Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity)
            .select(x => x.cargoTypeName)
            .distinct()
            .toArray();
        const cargoUnits = Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity)
            .select(x => x.cargoUnit)
            .distinct()
            .toArray();
        const periods = Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity)
            .select(x => {
                return this.isQuarterly ? x.customerDateQuarterSkey : x.year;
            })
            .distinct()
            .toArray();

        for (const cargoUnit of cargoUnits) {
            for (const cargoType of cargoTypes) {
                for (const period of periods) {
                    let emissions = 0;
                    const year = Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity)
                        .where(x => x.customerDateQuarterSkey == period && x.cargoUnit == cargoUnit && x.cargoTypeName == cargoType)
                        .select(x => x.year).firstOrDefault();

                    if (this.isQuarterly) {
                        emissions = Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity)
                            .where(x => x.customerDateQuarterSkey == period && x.cargoUnit == cargoUnit && x.cargoTypeName == cargoType)
                            .select(x => x.totalGhgEmissions)
                            .sum(x => x);
                    } else {
                        emissions = Enumerable.from(this.data.caReportPerCargoTypeForEmissionsIntensity)
                            .where(x => x.year == period && x.cargoUnit == cargoUnit && x.cargoTypeName == cargoType)
                            .select(x => x.totalGhgEmissions)
                            .sum(x => x);
                    }
                    cargoTypeDistributions.push({
                        cargoType: cargoType,
                        cargoTypeDistributionId: 0,
                        cargoUnit: cargoUnit,
                        periodValue: period,
                        totalGhgEmissions: emissions,
                        year: year
                    });
                }
            }
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CargoTypesDialog,
            {
                items: cargoTypeDistributions,
                isQuarterly: this.isQuarterly
            }, true, false, false);
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedEmissionIntensity),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedSizeGroupeTypeAvg),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary.length))
    get leftInsight() {
        return this.data ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel)
            .where(x => ['A', 'B', 'C'].indexOf(x.ghgRating) > -1)
            .where(x => (this.selectedEmissionIntensity.name != 'EEOI' && x.average <= this.selectedSizeGroupeTypeAvg) || (this.selectedEmissionIntensity.name == 'EEOI' && x.average <= this.data.caEmissionsIntensityReportSummary[0].trajectoryValue))
            .orderBy(x => x.ghgRating).thenBy(x => x.average).firstOrDefault() : null;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedEmissionIntensity),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedSizeGroupeTypeAvg),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary.length))
    get middleInsight() {
        return this.data ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel)
            .where(x => ['E', 'F', 'G'].indexOf(x.ghgRating) > -1)
            .where(x => (this.selectedEmissionIntensity.name != 'EEOI' && x.average > this.selectedSizeGroupeTypeAvg) || (this.selectedEmissionIntensity.name == 'EEOI' && x.average > this.data.caEmissionsIntensityReportSummary[0].trajectoryValue))
            .orderByDescending(x => x.ghgRating).thenByDescending(x => x.average).firstOrDefault() : null;
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportPerVessel.length),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedEmissionIntensity),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.selectedSizeGroupeTypeAvg),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.data.caEmissionsIntensityReportSummary.length))
    get rightInsight() {
        return this.data ? Enumerable.from(this.data.caEmissionsIntensityReportPerVessel)
            .where(x => ['A', 'B', 'C'].indexOf(x.ghgRating) > -1)
            .where(x => (this.selectedEmissionIntensity.name != 'EEOI' && x.average > this.selectedSizeGroupeTypeAvg) || (this.selectedEmissionIntensity.name == 'EEOI' && x.average > this.data.caEmissionsIntensityReportSummary[0].trajectoryValue))
            .orderBy(x => x.ghgRating).thenByDescending(x => x.average).firstOrDefault() : null;
    }

    navigate(num: number) {
        const ghg = num == 2 ? ['E', 'F', 'G'] : ['A', 'B', 'C'];

        let yearFrom = this.showYearRangeFilter ? this.yearFirstFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January? this.year: (this.year? this.year.split('-')[1] : null));
        let yearTo = this.showYearRangeFilter ? this.yearSecondFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January? this.year: (this.year? this.year.split('-')[1] : null));

        if (!yearFrom) yearFrom = "";
        if (!yearTo) yearTo = "";

        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.CarbonAccountingVoyageRecords, {
            type: this.selectedEmissionIntensity.name,
            ghg: ghg,
            orderDes: num == 1 ? [] : num == 3 ? ["average"] : ["ghgRating", "average"],
            orderAsc: num == 1 ? ["ghgRating", "average"] : num == 3 ? ["ghgRating"] : [],
            vesselTypeGroupId: this.selectedVesselType ? this.selectedVesselType.vesselTypeGroupId : null,
            vesselTypeId: this.selectedVesselType ? this.selectedVesselType.shipTypeId : null,
            yearFrom: yearFrom,
            yearTo: yearTo
        });
    }

    public customFilters: CarbonAccountingCustomReportFilterModel[] = [];

    private userCustomerId: number = null;

    //#region custom report filters

    async getCustomFilters()
    {
        if(!this.userCustomerId && !this.selectedCustomerId)
            this.userCustomerId = await this.customerService.getCustomerId();
        this.customFilters = await this.carbonAccountingService.GetCustomerCACustomReportFilters(this.selectedCustomerId? this.selectedCustomerId: this.userCustomerId);
        if(!Array.isArray(this.customFilters))
            this.customFilters = [];
    }

    toggleCustomFilter(filter)
    {
        filter.open = !filter.open;
        this.selectedFilter = {
            title: filter.displayName,
            clear: () => {
                this.clearCustomFilter(filter);
            },
            apply: () => {
                if(!this.customFilters || this.customFilters.length == 0)
                    return;
                const selectedItem = filter["selectedItem"];
                const index = this.customFilters.indexOf(filter);
                this.applyCustomFilter(index,selectedItem, false);
            }
        }
    }

    clearCustomFilter(filter)
    {
        filter.selectedValue = null;
        filter.selectedItem = null;
        filter.open = false;
        this.onPropertyChanged<CarbonAccountingEmissionsIntensity>(z => z.customFilters);

        this.refreshChartData();
    }

    applyCustomFilter(index, item: LookupValue, reload: boolean = true) 
    {
        if(!this.customFilters || this.customFilters.length == 0)
            return;
        const filter = this.customFilters[index];
        filter["selectedValue"] = item? item.name: null;
        if (reload == true) this.refreshChartData();
        this.toggleCustomFilter(filter);
    }
    //#endregion

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }
    @computedFrom(Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.yearRangeFilterActive),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.customFilters),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.changes.customFilters),
        Reflection.getPropertyPath<CarbonAccountingEmissionsIntensity>(z => z.customFilters.length))

    public get mobileFilterSelected(): boolean {
        return this.yearRangeFilterActive || (this.customFilters && this.customFilters.filter(x => x["open"] == true).length > 0);
    }

    sizeGroupTypesLookupValues: LookupValue[];

    async reloadSizeGroupTypes()
    {
        const lookupResponse = await this.lookupService.getLookup(new LookupValueRequest("RS_CarbonAccountingSizeGroupTypes"));
        let items = lookupResponse.activeValues.slice(0);

        const selectedVesselTypeGroupIds = this.selectedVesselType ? this.selectedVesselType.vesselTypeGroupId : null;
        const selectedVesselTypeIds = this.selectedVesselType ? this.selectedVesselType.shipTypeId : null;        
        
        if(items && items.length > 0 && ((selectedVesselTypeGroupIds != null && selectedVesselTypeGroupIds.length > 0) || (selectedVesselTypeIds != null && selectedVesselTypeIds.length > 0)))
        {
            items = items.filter(x => {
                let show: boolean = true;
                if(selectedVesselTypeIds != null && selectedVesselTypeIds.length > 0)
                {
                    const vesselTypeIds = x.additionalData && x.additionalData.vesselTypeIds && x.additionalData.vesselTypeIds.trim() != ''?
                        x.additionalData.vesselTypeIds.split(',').map(x => x != null && x.trim() != '' && !isNaN(x)? Number(x): null).filter(x => x != null): [];

                        show = vesselTypeIds.filter(x => selectedVesselTypeIds.indexOf(x) > -1).length > 0;

                        if(show) return show;
                }
                
                if(selectedVesselTypeGroupIds != null && selectedVesselTypeGroupIds.length > 0)
                {
                    const vesselTypeGroupIds = x.additionalData && x.additionalData.vesselTypeGroupIds && x.additionalData.vesselTypeGroupIds.trim() != ''?
                        x.additionalData.vesselTypeGroupIds.split(',').map(x => x != null && x.trim() != '' && !isNaN(x)? Number(x): null).filter(x => x != null): [];

                    show = vesselTypeGroupIds.filter(x => selectedVesselTypeGroupIds.indexOf(x) > -1).length > 0;
                }

                return show;
            });
        }

        items.unshift({
            id: null,
            name: "ALL",
            isActive: true,
            isDefault: false,
            isSelectable: true,
            guid: Guid.generateGuid(),
            additionalData: null,
            children: null
        });
        
        this.sizeGroupTypesLookupValues = items;
    }

    @computedFrom("selectedComputedUnit")
    get unitOfMeassurment()
    {
        return this.selectedComputedUnit ? this.selectedComputedUnit.unit: "t";
    }
}
